import { Plan } from 'api/savings-plans';
import { LOCAL_CART_KEY } from 'constants/cart';
import { useAuth } from 'contexts/auth';
import { useCart } from 'contexts/cart';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

interface UseCartSavingsPlanCleanupProps {
  availablePlans: Plan[];
  hasHydrated: boolean;
}

interface LocalCartItem {
  variantId: string;
  quantity: number;
  interval?: string;
  redeemItem?: boolean;
  points?: number;
}

/**
 * Removes savings plan from cart when customer logs in with an existing plan.
 * This handles the edge case where a non-logged-in user adds a savings plan to cart,
 * then a customer with an existing savings plan logs in.
 */
export const useCartSavingsPlanCleanup = ({
  availablePlans,
  hasHydrated,
}: UseCartSavingsPlanCleanupProps): void => {
  const { items } = useCart();
  const { customer } = useAuth();
  const { reload } = useRouter();

  useEffect(() => {
    // When customer logs in with an existing savings plan,
    // remove any savings plan from local storage cart
    if (customer?.savingsPlan && hasHydrated) {
      const planInEffectiveCart = items?.find((item) =>
        availablePlans.some((plan) => String(plan.variantId) === String(item.variantId))
      );

      if (planInEffectiveCart) {
        // Access local storage directly to remove the savings plan
        try {
          const localCartKey = LOCAL_CART_KEY;
          const localCartJson = localStorage.getItem(localCartKey);

          if (localCartJson) {
            const localCart: LocalCartItem[] = JSON.parse(localCartJson);

            // Find if the savings plan exists in local cart
            const hasSavingsPlanInLocalCart = localCart.some((item) =>
              availablePlans.some((plan) => String(plan.variantId) === String(item.variantId))
            );

            if (hasSavingsPlanInLocalCart) {
              // Remove savings plan(s) from local cart
              const filteredCart = localCart.filter(
                (item) =>
                  !availablePlans.some((plan) => String(plan.variantId) === String(item.variantId))
              );

              // Update local storage
              localStorage.setItem(localCartKey, JSON.stringify(filteredCart));

              reload();
            }
          }
        } catch (error) {
          console.error('Failed to remove duplicate savings plan from cart:', error);
        }
      }
    }
  }, [customer?.savingsPlan, hasHydrated, items, availablePlans, reload]);
};
